import { useParams, Navigate, Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { GoldButton } from "@/components/primitives";
import CTABlock from "@/components/CTABlock";
import { services } from "@/data/content";
import { site } from "@/config/site";

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const Icon = service.icon;
  const others = services.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <div data-testid={`service-detail-${slug}`}>
      <PageHero
        testId="service-detail-hero"
        label="Service"
        title={service.title}
        subtitle={service.short}
      />

      <section className="border-t border-fs-border bg-fs-bg2 py-20 lg:py-28">
        <div className="fs-shell grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="flex h-14 w-14 items-center justify-center border border-fs-border text-fs-gold">
                <Icon className="h-6 w-6" strokeWidth={1.4} />
              </span>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-8 fs-h3">Overview</h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-4 max-w-2xl fs-body">{service.overview}</p>
            </Reveal>

            <Reveal delay={0.2}>
              <h3 className="mt-14 text-xs uppercase tracking-[0.24em] text-fs-gold">
                What's included
              </h3>
            </Reveal>
            <ul className="mt-6 space-y-4">
              {service.deliverables.map((d, i) => (
                <Reveal as="li" key={d} delay={0.05 * i} className="flex items-start gap-4">
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center border border-fs-gold/40 text-fs-gold">
                    <Check className="h-3 w-3" />
                  </span>
                  <span className="text-fs-ink">{d}</span>
                </Reveal>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-5">
            <Reveal delay={0.2}>
              <div className="border border-fs-border bg-fs-card p-8">
                <h3 className="text-xs uppercase tracking-[0.24em] text-fs-gold">
                  The outcome
                </h3>
                <p className="mt-5 font-serif text-2xl font-light leading-snug text-fs-ink">
                  {service.outcome}
                </p>
                <div className="mt-8">
                  <GoldButton href={site.bookingUrl} target="_blank" rel="noopener noreferrer" variant="solid" testId="service-detail-cta">
                    Book a Strategy Call
                  </GoldButton>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="border-t border-fs-border bg-fs-bg py-20">
        <div className="fs-shell">
          <div className="flex items-center justify-between">
            <h2 className="fs-h3">Explore more services</h2>
            <Link to="/services" className="hidden text-sm text-fs-gold sm:inline-flex">
              All services
            </Link>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {others.map((o) => {
              const OIcon = o.icon;
              return (
                <Link
                  key={o.slug}
                  to={`/services/${o.slug}`}
                  data-testid={`related-service-${o.slug}`}
                  className="group border border-fs-border bg-fs-card p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-fs-gold/40"
                >
                  <OIcon className="h-5 w-5 text-fs-gold" strokeWidth={1.4} />
                  <h3 className="mt-5 font-serif text-xl text-fs-ink">{o.title}</h3>
                  <span className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-fs-muted group-hover:text-fs-gold">
                    Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CTABlock />
    </div>
  );
}
